import * as Yup from "yup";
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
  NOSPACE_REGEX,
  PASSWORDS_REGEX,
} from "./constants";
import { MESSAGE } from "./validationMessage";
import { stripHtml } from "string-strip-html"; 

let title = Yup.string()
  .max(60, "This field should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let status = Yup.mixed().required(MESSAGE.REQUIRED);

const timeSlots = [
  "12:00 AM", "12:30 AM", "1:00 AM", "1:30 AM", "2:00 AM", "2:30 AM", "3:00 AM", 
  "3:30 AM", "4:00 AM", "4:30 AM", "5:00 AM", "5:30 AM", "6:00 AM", "6:30 AM", 
  "7:00 AM", "7:30 AM", "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", 
  "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", 
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", 
  "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM", 
  "9:00 PM", "9:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM"
];


export const CmsSchema = Yup.object({
  title,
  titleKh: Yup.string()
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
  status,
  description: Yup.string()
  .required('Description is required')
  .test('no-spaces-at-start', 'Spaces are not allowed before content', (value) => {
    const strippedValue = stripHtml(value || '').result;
    return strippedValue.trimStart().length > 0; 
  })
  .test('min-length', 'Description must be at least 10 characters', (value) => {
    const strippedValue = stripHtml(value || '').result;
    return strippedValue.trim().length >= 10; 
  })
  .test(
    'is-empty-html',
    'Description cannot be empty',
    (value) => {
      const strippedValue = stripHtml(value || '').result;
      return strippedValue.trim() !== ''; 
    }
  )
  .test('no-nbsp', 'Description cannot contain only spaces', (value) => {
    const strippedValue = stripHtml(value || '').result;
    return strippedValue.replace(/<\/?[^>]+(>|$)/g, '').length > 0; 
  }),
 descriptionKh: Yup.string()
  .required('Description is required')
  .test('no-spaces-at-start', 'Spaces are not allowed before content', (value) => {
    const strippedValue = stripHtml(value || '').result;
    return strippedValue.trimStart().length > 0; 
  })

});

export const bannerAndOffer = (id) => Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Banner name is too short. It must be at least 3 characters long.')
    .max(100, 'Banner name is too long. It cannot exceed 100 characters.')
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

    description: Yup.string().required("Description is required").matches(/^[^\s][\s\S]*$/  , "Spaces are not allowed at the start of the text."),
    nameKh: Yup.string()
    .required('Name is required')
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

    descriptionKh: Yup.string().required("Description is required").matches(/^[^\s][\s\S]*$/  , "Spaces are not allowed at the start of the text."),
  
    startDate: Yup.date()
    .test('required-if-no-id', 'Start Date is required', function (value) {
      const { path, createError } = this;
      if (!id) {
        if (!value) {
          return createError({ path, message: 'Start Date is required' });
        }
        const currentDate = new Date();
        if (new Date(value) < currentDate) {
          return createError({ path, message: 'Start Date cannot be in the past' });
        }
      }
      return true;
    }),

  endDate: Yup.date()
    .test('required-if-no-id', 'End Date is required', function (value) {
      const { path, createError } = this;
      const { startDate } = this.parent;
      if (!id) {
        if (!value) {
          return createError({ path, message: 'End Date is required' });
        }
        // Check if endDate is at least 30 minutes after startDate
        const startTime = new Date(startDate);
        const endTime = new Date(value);
        if (endTime <= startTime || (endTime - startTime) < 30 * 60 * 1000) {
          return createError({ path, message: 'End Date must be at least 30 minutes after Start Date' });
        }
      }
      return true;
    }),


  
  offerType: Yup.string()
    .required('Offer Type is required'),

  couponCode: Yup.string()
    .min(6, 'Coupon Code should be at least 6 characters long')
    .max(8, 'Coupon Code should be at most 8 characters long')
    .matches(/^[A-Za-z0-9]*$/, 'Coupon Code can only contain letters and numbers, no spaces or special characters')
    .test('couponCode', 'Coupon Code is required', function(value) {
      const { offerType } = this.parent; 
      if (offerType === 'offer') {
        return value && value.trim() !== '';
      }
      return true;
    }),

  couponType: Yup.string()
    .test('couponType', 'Coupon Type is required', function(value) {
      const { offerType } = this.parent; 

      if (offerType === 'offer') {
        return value && value.trim() !== '';
      }
      return true;
    }),

  discount: Yup.string()
    .test('discount', 'Discount is required', function(value) {
      const { couponType } = this.parent; 
      if (couponType === 'percentage') {
        return value && value.trim() !== '';
      }
      return true;
    })
    .matches(/^[1-9]\d*$/, 'Discount must be a valid number without leading zeros or repeated digits')
    .test('isValidNumber', 'Discount must be between 0 and 100', function(value) {
      const { couponType } = this.parent; 
      if (couponType === 'percentage') {
        const numValue = parseInt(value, 0);
        return numValue >= 0 && numValue <= 100;
      }
      return true;

  }),

  minimumPrice: Yup.string()
    .test('minimumPrice', 'Minimum Price is required', function(value) {
      const { couponType } = this.parent; 
      if (couponType === 'percentage') {
        return value && value.trim() !== '';
      }
      return true;
    })
    .matches(/^[1-9]\d*$/, 'Minimum price must be a valid number without leading zeros or repeated digits')
    .test('isValidNumber', 'Minimum price must be between 10 and 100', function(value) {
      const { couponType } = this.parent; 
      if (couponType === 'percentage') {
        const numValue = parseInt(value, 10);
        return numValue >= 10 && numValue <= 100;
      }
      return true;

    }),

  purchaseProductIds: Yup.array()
    .of(Yup.string().required('You must select a Purchase Product'))
    .test('purchaseProductIds', 'You must select a Purchase Product', function(value) {
      const { couponType } = this.parent; 
      if (couponType === 'B1G1' || couponType === 'B1GO') {
        return value && value.length > 0; 
      }
      return true;
  }),

  freeProductId: Yup.mixed()
    .test('freeProductId', 'You must select a Free Product', function(value) {
      const { couponType } = this.parent;
      if (couponType === 'B1GO') {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return false;
        }
        return Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' && value[0].trim().length > 0;
      }
      return true;
  }),

  image: Yup.mixed()
    .test('image', 'Image is required', function(value) {
      const imagePreview = this.parent.imagePreview;
      return imagePreview || value != null; 
    }),
});


export const category = Yup.object().shape({
  name: Yup.string().required('Name is required')
    .min(3, 'Name should be at least 3 characters long')
    .max(50, 'Name should be at most 50 characters long')
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

  nameKh: Yup.string().required("Khmer name is required"),

    index: Yup.string()
    .required('Category position is required')
    .matches(/^\d+$/, 'Category position must be a number'),

  image: Yup.mixed()
    .test('image', 'Image is required', function(value) {
      const imagePreview = this.parent.imagePreview;
      return imagePreview || value != null; 
    }),
})

export const productValidation = Yup.object({
//   galleryImages: Yup.array().when('oldGalleryImages', {
//     is: (oldGalleryImages) => !oldGalleryImages || oldGalleryImages.length === 0,
//     then: () => Yup.array().required("Gallery image is required").min(1, "Gallery image is required"),
//     otherwise: () => Yup.array()
// }),
  name: Yup.string()
    .required("Name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Name must be at least 2 characters")
    .test("name", "Name should be less than 100 characters", (values) => {
      if (values.length >= 100) {
        return false;
      } else {
        return true;
      }
    }),
  nameKh: Yup.string().required("Khmer name is required")
   .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

    index: Yup.string()
    .required('Product position is required')
    .matches(/^\d+$/, 'Product position must be a number'),
  qty: Yup.string().required("Quantity is required"),
   weight: Yup.string()
    .when("includeWeight", {
      is: true,
      then: (schema) =>
        schema
          .required("Weight is required")
          .matches(/^\d+(g|kg|can)$/, "Weight must be a number followed by 'g','kg' or 'can'")
          .max(7, "Weight must be at most 7 characters"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

  productModel: Yup.string()
  .trim()
  .required("Product Model is required")
  // .matches(/^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*$/, "Product Model must be alphanumeric.")
  .min(2, "Product Model must be at least 2 characters").max(25, "Product Model must be at most 25 characters"),

  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be greater than 0"),
  image: Yup.mixed().required("Product image is required"),
  description: Yup.string().required("Description is required").matches(/^[^\s][\s\S]*$/  , "Spaces are not allowed at the start of the text."),
  descriptionKh: Yup.string().required("Khmer description is required").matches(/^[^\s][\s\S]*$/  , "Spaces are not allowed at the start of the text."),
  categoryId: Yup.string().required("Category is required"),
  redeemRewardPoint: Yup.mixed().when('isFreeProduct', {
    is: true,
    then: () => 
      Yup.number()
        .typeError('Reward Points must be a number')
        .required('Reward Points is required')
        .min(1, 'Reward Points must be at least 1'),
    otherwise: () => Yup.mixed().notRequired(),
  }),
  
  
  
})

export const dealerSchema = (id) => Yup.object().shape({
  businessName: Yup.string()
    .required("Business Name is required")
    .min(2,   "Business Name must be atleast 2 character")
    .max(55,  `Business Name must not be more than 55 characters.`)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
email: Yup.string()
  .nullable()
  .notRequired()
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
  .matches(EMAIL_REGEX, 'Invalid email format'),

  phoneNumber: Yup.string()
    .required("Phone number is required"),
    // .matches(/^[0-9]{10}$/,"Phone number must be 10 digits long and contain only numbers"),

  dealerId: Yup.string()
    .when([], {
      is: () => !id, // if `id` is truthy
      then: schema => schema.required("Dealer ID is required").min(6, "Please enter 6 digits Dealer Id."),
      otherwise: schema => schema.notRequired(),
    }),
  dealerOrderRange: Yup.string()
  .required("Dealer Order Range is required")
  .matches(/^\d+$/, "Only numbers are allowed")
  .max(15, "Maximum 15 digits allowed"),

  houseNumber: Yup.string()
    .min(1, 'Flat No./Plot No. must be atleast 1 character')
    .max(40, 'Flat No./Plot No. must not be more than 40 characters.')
    .required('Flat No./Plot No. is required')
    .test('no-trailing-spaces', 'Flat No./Plot No cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    ),
    streetNumber: Yup.string()
    .min(1, 'Flat No./Plot No. must be atleast 1 character')
    .max(40, 'Flat No./Plot No. must not be more than 40 characters.')
    .required('Flat No./Plot No. is required')
    .test('no-trailing-spaces', 'Flat No./Plot No cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    ),

  village: Yup.string()
    .min(3, 'Village must be atleast 1 character')
    .max(50, 'Village must not be more than 50 characters.')
    .test('not-purely-numeric', 'Village cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-trailing-spaces', 'Village cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .nullable(),

  district: Yup.string()
    .min(2, 'District must be atleast 2 character')
    .max(50, 'District must not be more than 50 characters.')
    .test('not-purely-numeric', 'District cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'District cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'District cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('District is required'),

  commune: Yup.string()
    .min(2, 'Commune must be atleast 2 character')
    .max(50, 'Commune must not be more than 50 characters.')
    .test('not-purely-numeric', 'Commune cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'Commune cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'Commune cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('Commune is required'),

  city: Yup.string()
    .min(2, 'City must be atleast 2 character')
    .max(50, 'City must not be more than 50 characters.')
    .test('not-purely-numeric', 'City cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'City cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'City cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('City is required'),

  postalCode: Yup.string()
    .max(10, 'Postal Code must not be more than 10.')
    .nullable(),
  // commission: Yup.string()
  //     .required("Commission is required")
  //     .matches(/^\d+(\.\d+)?$/, "Commission must be a valid number")
  //     .test("min-max", "Commission must be between 0.01 and 100", (value) => {
  //       const numValue = parseFloat(value);
  //       return numValue > 0 && numValue <= 99.99;
  //     })
  //     .nullable(),

  documents: Yup.mixed()
    .required("Document is required")
    .test("required", "Document is required", (value) => {
      return value !== null && value !== "";
    })
    
});

export const CustomerSchema = Yup.object().shape({

  name: Yup.string()
    .required('Name is required')
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Name must be atleast 2 character")
    .max(55, "Name should must not be more than 55 characters."),

  email: Yup.string()
    .matches(EMAIL_REGEX, MESSAGE.EMAIL)
    .email("Invalid email format"),  

  phoneNumber: Yup.string()
    // .matches(MOBILE_REGEX, 'Phone number is not valid')
    .required('Phone is required'),

  houseNumber: Yup.string()
    .min(1, 'Flat No./Plot No. must be atleast 1 character')
    .max(40, 'Flat No./Plot No. must not be more than 40 characters.')
    .required('Flat No./Plot No. is required')
    .test('no-trailing-spaces', 'Flat No./Plot No cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    ),
    streetNumber: Yup.string()
    .min(1, 'Flat No./Plot No. must be atleast 1 character')
    .max(40, 'Flat No./Plot No. must not be more than 40 characters.')
    .required('Flat No./Plot No. is required')
    .test('no-trailing-spaces', 'Flat No./Plot No cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    ),

  village: Yup.string()
    .min(3, 'Village must be atleast 1 character')
    .max(50, 'Village must not be more than 50 characters.')
    .test('not-purely-numeric', 'Village cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-trailing-spaces', 'Village cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .nullable(),

  district: Yup.string()
    .min(2, 'District must be atleast 2 character')
    .max(50, 'District must not be more than 50 characters.')
    .test('not-purely-numeric', 'District cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'District cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'District cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('District is required'),

  commune: Yup.string()
    .min(2, 'Commune must be atleast 2 character')
    .max(50, 'Commune must not be more than 50 characters.')
    .test('not-purely-numeric', 'Commune cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'Commune cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'Commune cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('Commune is required'),

  city: Yup.string()
    .min(2, 'City must be atleast 2 character')
    .max(50, 'City must not be more than 50 characters.')
    .test('not-purely-numeric', 'City cannot be purely numeric', (value) => isNaN(Number(value)))
    .test('no-consecutive-spaces', 'City cannot contain consecutive spaces', (value) =>
      value ? !/\s{2,}/.test(value) : true
    )
    .test('no-trailing-spaces', 'City cannot start or end with spaces or special characters', (value) =>
      value ? !/^[\s\-']|[\s\-']$/.test(value) : true
    )
    .required('City is required'),

  postalCode: Yup.string()
    .max(10, 'Postal Code must not be more than 10.')
    .nullable(),
});


export const validationFaqSchema = Yup.object().shape({
  title: Yup.string()
    .required("Question is required")
    .matches(NOSPACE_REGEX, "Spaces are not allowed at the start or end of the text.")
    .min(2, "Please enter at least 2 characters")
    .max(100, "Question must not be more than 100 characters."),
  titleKh: Yup.string()
    .required("Question is required")
    .matches(NOSPACE_REGEX, "Spaces are not allowed at the start or end of the text."),
  description: Yup.string()
    .required('Description is required')
    .test('no-spaces-at-start', 'Spaces are not allowed before content', (value) => {
      const strippedValue = stripHtml(value || '').result;
      
      return !/^\s/.test(strippedValue); 
    })    

    .test('min-length', 'Description must be at least 10 characters', (value) => {
      const strippedValue = stripHtml(value || '').result;
      return strippedValue.trim().length >= 10; 
    })
    .test('max-length', 'Description must not exceed 1000 characters', (value) => {
      const strippedValue = stripHtml(value || '').result;
      return strippedValue.trim().length <= 1000; 
    }),
    descriptionKh: Yup.string().trim().required("Description is required")
    .test('no-spaces-at-start', 'Spaces are not allowed before content', (value) => {
      const strippedValue = stripHtml(value || '').result;
      
      return !/^\s/.test(strippedValue); 
    }),
  });



  export const driverSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2,   "Name must be atleast 2 character")
      .max(55,  `Name must not be more than 55 characters.`)
      .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
    email: Yup.string()
      .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
      .matches(EMAIL_REGEX, 'Invalid email format')
      .nullable()
      .notRequired(),

   phoneNumber: Yup.string()
  .required("Phone number is required")
  .matches(/^[0-9]+$/, "Phone number must contain only numbers")
  .max(20, "Phone number cannot be more than 20 digits"),

      address: Yup.string()
      .matches(/^(?!\s*$)[a-zA-Z0-9\s\-\'\,\.\#\/]*$/)
      .min(2, 'Address must be at least 2 characters long')
      .max(50, 'Address must not exceed 50 characters')
      .test(
        'not-only-special-characters',
        'Address cannot contain only special characters',
        (value) => /[a-zA-Z0-9]/.test(value) 
      )
      .test(
        'not-purely-numeric',
        'Address cannot be purely numeric',
        (value) => value && isNaN(Number(value))
      )
      .required('Address is required'),
    dealerId: Yup.string()
      .required("Dealer Id  is required")
      .min(6,"Please enter 6 digits Dealer Id."),
    
      drivingLicense: Yup.mixed()
      .required("Driving License is required")
      .test("fileType", "Only PDF files are allowed", (value) => {
        if (!value) return false;
        if (typeof value === "string") return true; // Already a valid path
        return value?.type === "application/pdf"; // Check uploaded file type
      }),
    idProof: Yup.mixed()
      .required("ID Proof is required")
      .test("fileType", "Only PDF files are allowed", (value) => {
        if (!value) return false;
        if (typeof value === "string") return true; // Already a valid path
        return value?.type === "application/pdf"; // Check uploaded file type
      }), 
      
  });

  export const EmailTemplateSchema = Yup.object({
    title: Yup.string()
      .min(2, "Title must be longer than 2 characters")
      .max(100, "Title should not be longer than 100 characters")
      .required("Title is required")
      .matches(NOSPACE_REGEX, "Title cannot start or end with a blank space."),
    subject: Yup.string()
      .min(2, "Subject must be longer than 2 characters")
      .max(100, "Subject should not be longer than 100 characters")
      .required("Subject is required")
      .matches(NOSPACE_REGEX, "Subject cannot start or end with a blank space."),
    description: Yup.string()
      .min(10, "Description must be longer than 10 characters")
      .max(500, "Description should not be longer than 500 characters")
      .required("Description is required")
      .matches(NOSPACE_REGEX, "Description cannot start or end with a blank space."),
  
  })

export const timeManagementSchema = Yup.object().shape({
  startTime: Yup.string().required("Start Time is required"),
  endTime: Yup.string()
    .required("End Time is required")
});


export const subadminSchema = (id) => Yup.object().shape({
  name: Yup.string()
  .max(60, "Name should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
  password: Yup.string()
  .required(MESSAGE.REQUIRED)
  .matches(PASSWORDS_REGEX, MESSAGE.PASSWORD) 
  .test('not-all-zeros', MESSAGE.PASSWORD, value => value !== '0000000000'),

  permission: Yup.array()
    .of(Yup.string())
    .min(1, "At least one permission is required")
    .required("Permissions are required"),
});